import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { RootEntityType, Thread, ThreadUser } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  pickThreadUserProps,
  ThreadParams,
  useSelectThread,
  useThreadFromQueryString,
  useThreads,
  WithThreads,
} from '@schooly/components/threads';
import { AccessDenied, Loading, ModalSideBar } from '@schooly/style';
import { Backdrop, Stack } from '@mui/material';
import React, { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { STREAM_API_KEY } from '../../../../config';
import { useMessage } from '../../../../context/messages/useMessage';
import { MessageModalPreviewMode } from '../MessagePreviewModalTabs';
import { CollapsiblePanel } from 'apps/web/src/components/common/CollapsiblePanel';
import { MessageModalPreviewSelectedThread } from './MessageModalPreviewSelectedThread';
import { MessageThreadList } from './MessageThreadList';

export const MessageModalPreviewThreads: FC = () => {
  const { schoolId, currentUser, currentStaff } = useAuth();
  const { messageId, hasAccessToThreads, hasThreads } = useMessage();

  if (!hasThreads || !hasAccessToThreads) {
    return <AccessDenied />;
  }

  if (!messageId || !currentUser || !schoolId || !STREAM_API_KEY || !currentStaff) {
    if (!STREAM_API_KEY) console.error('STREAM_API_KEY was not provided');
    return (
      <Stack height="100%" justifyContent="center" alignItems="center" width="100%">
        <Loading />
      </Stack>
    );
  }

  const threadsParams = {
    schoolId,
    rootEntityType: RootEntityType.MESSAGE,
    rootEntityId: messageId,
    apiKey: STREAM_API_KEY,
  };

  return (
    <WithThreads {...threadsParams}>
      <ThreadsComponent
        {...threadsParams}
        currentUser={pickThreadUserProps({
          user_id: currentUser.user_id,
          ...currentStaff,
        })}
      />
    </WithThreads>
  );
};

type ThreadsComponentProps = {
  currentUser: ThreadUser;
} & ThreadParams;

const ThreadsComponent = ({ currentUser, ...threadsParams }: ThreadsComponentProps) => {
  const navigate = useNavigate();

  const handleThreadCreated = useCallback(
    (_: Thread) => {
      trackAmplitudeEvent({
        name: AnalyticsEventName.StartFamilyThread,
        payload: { started_at: new Date().toISOString(), user_id: currentUser.user_id },
      });
    },
    [currentUser.user_id],
  );

  const selectThreadLifecycle = useSelectThread({
    currentUser,
    onThreadCreated: handleThreadCreated,
  });

  const { isThreadFetching } = useThreadFromQueryString({
    schoolId: threadsParams.schoolId ?? '',
    rootEntityType: threadsParams.rootEntityType,
    rootEntityId: threadsParams.rootEntityId,
    onThreadNotFound: () => navigate({ search: `?tab=${MessageModalPreviewMode.THREADS}` }),
    onThreadFound: selectThreadLifecycle,
  });

  const handleThreadClick = useCallback(
    (thread: Thread) =>
      navigate({ search: `?tab=${MessageModalPreviewMode.THREADS}&id=${thread.id}` }),
    [navigate],
  );

  useEffect(() => {
    trackAmplitudeEvent({
      name: AnalyticsEventName.ViewThreadsTab,
      payload: {
        message_id: threadsParams.rootEntityId,
      },
    });
  }, [threadsParams.rootEntityId]);

  const { hasConnection } = useThreads();

  return (
    <Stack direction="row" height="100%">
      {!hasConnection && (
        <Backdrop
          open
          sx={{ top: 100, left: 0, right: 0, bottom: 0, zIndex: (theme) => theme.zIndex.tooltip }}
          invisible
        />
      )}

      <CollapsiblePanel width={308}>
        <ModalSideBar sx={{ paddingTop: 1, paddingX: 0, maxWidth: 308, overflow: 'hidden' }}>
          <MessageThreadList
            schoolId={threadsParams.schoolId}
            rootEntityType={threadsParams.rootEntityType}
            rootEntityId={threadsParams.rootEntityId}
            onThreadClick={handleThreadClick}
          />
        </ModalSideBar>
      </CollapsiblePanel>

      <MessageModalPreviewSelectedThread isThreadFetching={isThreadFetching} />
    </Stack>
  );
};
